import React, { useCallback, useState, useRef, useMemo, type SyntheticEvent } from 'react';
import InvitePeopleModalAsync from '@jira/project-settings__invite-people-modal-project/src/AsyncCoreInvitesProjectDialogComponent.tsx';
import PersonAddIcon from '@atlaskit/icon/core/person-add';
import { token } from '@atlaskit/tokens';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { ThemedButton } from '@atlassian/jira-project-theme-components/src/ui/themed-button/ThemedButton.tsx';
import { getEdition } from '@atlassian/jira-shared-types/src/edition.tsx';
import { JIRA_SOFTWARE } from '@atlassian/jira-shared-types/src/application.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { fireUIAnalytics, type UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import { expVal } from '@atlassian/jira-feature-experiments';
import messages from './messages.tsx';
import type { InviteButtonProps } from './types.tsx';

const InviteButton = ({ projectId, projectName, projectKey }: InviteButtonProps) => {
	const { formatMessage } = useIntl();

	const cloudId = useCloudId();
	const appEditions = useAppEditions();
	const isAdmin = useIsAdmin();

	const inviteButtonRef = useRef<HTMLButtonElement | null>(null);

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const openDialog = useCallback(() => {
		setIsOpen(true);
	}, []);
	const closeDialog = useCallback(() => setIsOpen(false), []);

	// todo: restructure this logic into a more suitable place
	const experimentalFeatures = useMemo(() => {
		return expVal('jfind-copy-link-to-invite-modal-experiment', 'enrolled', false)
			? { restoreInviteButton: true }
			: {};
	}, []);

	// Generate copy link with project context
	const copyLink = useMemo(() => {
		if (
			typeof window !== 'undefined' &&
			expVal('jfind-copy-link-to-invite-modal-experiment', 'enrolled', false)
		) {
			const baseUrl = window.location.origin;
			const welcomeUrl = `${baseUrl}/welcome/software?projectId=${projectId}`;
			return `${baseUrl}/?continue=${encodeURIComponent(welcomeUrl)}`;
		}
		return undefined;
	}, [projectId]);

	return (
		<JSErrorBoundary
			id="invite-button"
			packageName="jiraOnboarding"
			teamName="Growth-Jira-Find"
			fallback={() => <></>}
		>
			<ThemedButton
				iconBefore={
					<PersonAddIcon
						spacing="spacious"
						label={formatMessage(messages.inviteFormTitle)}
						color={token('color.icon.subtle')}
					/>
				}
				appearance="default"
				onClick={(e: SyntheticEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
					fireUIAnalytics(analyticsEvent, 'horizontalInviteButton', {
						projectId,
						projectName,
						projectKey,
					});
					openDialog();
				}}
				ref={inviteButtonRef}
			/>
			<InvitePeopleModalAsync
				inviteRef={inviteButtonRef}
				cloudId={cloudId}
				isOpen={isOpen}
				projectId={projectId}
				jiraProjectName={projectName}
				jiraProjectKey={projectKey}
				onClose={closeDialog}
				appEdition={getEdition(JIRA_SOFTWARE, appEditions)}
				isSiteAdmin={isAdmin}
				experimentalFeatures={experimentalFeatures}
				experimental_copyLink={copyLink}
				groupByTypeOrder={['user', 'team', 'group', 'custom']}
			/>
		</JSErrorBoundary>
	);
};

export default InviteButton;
